import { httpClient } from './http-client';
import { BaseApi } from './base-api';
import type { ExtendedListParams } from '../types/search-criteria';
import type {
  Invoice,
  InvoicesList,
  ContractInvoicesList,
} from '../types/invoice';
import { removeNulls, unique} from './utils';
import { toInvoice } from './invoice-utils';
import { INVOICE_ENDPOINTS, CONTRACT_ENDPOINTS } from '@/config/api';

export class InvoiceApi extends BaseApi {
  /**
   * Получить список инвойсов с фильтрацией и пагинацией
   */
  static async getInvoices(params?: ExtendedListParams): Promise<InvoicesList> {
    return this.getList<Invoice>(INVOICE_ENDPOINTS.INVOICES, params);
  }

  /**
   * Получить инвойс по ID
   */
  static async getInvoiceById(invoiceId: number): Promise<Invoice> {
    return this.getById<Invoice>(INVOICE_ENDPOINTS.INVOICE_BY_ID(invoiceId));
  }

  // ============ Методы с критериями поиска ============

  /**
   * Поиск инвойсов по тексту в нескольких полях
   */
  static async searchInvoices(searchText: string, additionalParams?: Omit<ExtendedListParams, 'criteria'>): Promise<InvoicesList> {
    const params = this.createTextSearchParams(searchText, additionalParams, ['name', 'body']);
    return this.getInvoices(params);
  }

  static async getInvoicesByIds(ids: number[], additionalParams?: Omit<ExtendedListParams, 'criteria'>): Promise<InvoicesList> {
    const filteredIds = removeNulls(unique(ids));
    if(filteredIds.length === 0) {
      return {
        items: [],
        total: 0,
      };
    }
    const params = this.createIdSearchParams(filteredIds, additionalParams, 'id');
    return this.getInvoices(params);
  }

  /**
   * Every invoice of a contract, including its lines' (rack/insurance) — resolved
   * server-side against the correct owning contract, not derivable by filtering
   * /customers/invoices on entityId/entityType (a line invoice's entityId is the
   * line's id, not the contract's).
   */
  static async getInvoicesByContractId(contractId: number): Promise<InvoicesList> {
    const response = await httpClient.get<ContractInvoicesList>(CONTRACT_ENDPOINTS.CONTRACT_INVOICES(contractId));
    return {
      total: response.items.length,
      items: response.items.map(toInvoice),
    };
  }
}
